import { useFormContext } from "react-hook-form";
import Textarea from "@src/components/Textarea/Textarea";
import FileInput from "@src/components/FileInput/FileInput";
import FormField from "@src/components/FormField/FormField";
import Hint from "@src/components/Helpers/Hint/Hint";
import Input from "@src/components/Input/Input";
import { toImperial, toMetric } from "@src/utils/character-utils";
import { useEffect, useRef } from "react";

interface IProps {
    hints: boolean;
    bodyImage: string;
    measurementUnit: "metric" | "imperial";
}
const VersionForm = ({
    hints = false,
    bodyImage,
    measurementUnit = "metric",
}: IProps) => {
    const hasNotBeenInitialised = useRef(true);
    /**
     * In charge of translating feet/inches into CM and setting them to the "height" input to send it back to the server
     */
    function handleHeight(inputName: any) {
        const ftInput = document.querySelector(
            `input[name="${inputName}-feet"]`,
        ) as HTMLInputElement;
        const inInput = document.querySelector(
            `input[name="${inputName}-inches"]`,
        ) as HTMLInputElement;
        if (ftInput !== null && inInput !== null) {
            const cmValue = toMetric(
                parseInt(ftInput.value),
                parseInt(inInput.value),
            );
            setValue(inputName, cmValue, { shouldValidate: true });
        }
    }
    function setValueInInput(inputName: string, feet: number, inches: number) {
        const ftInput = document.querySelector(
            `input[name="${inputName}-feet"]`,
        ) as HTMLInputElement;
        const inInput = document.querySelector(
            `input[name="${inputName}-inches"]`,
        ) as HTMLInputElement;
        if (ftInput && inInput) {
            ftInput.valueAsNumber = feet;
            inInput.valueAsNumber = inches;
            hasNotBeenInitialised.current = false;
        }
    }
    type ImperialMetrics = { feet: number; inches: number };
    interface ISetInitialImperialHeightArgs {
        height: ImperialMetrics;
        overrideHeight: ImperialMetrics;
    }
    function setInitialImperialHeight({
        height: { feet, inches },
        overrideHeight: { feet: overrideFeet, inches: overrideInches },
    }: ISetInitialImperialHeightArgs) {
        setValueInInput("height", feet, inches);
        setValueInInput("override_height", overrideFeet, overrideInches);
    }
    useEffect(() => {
        if (measurementUnit === "imperial" && hasNotBeenInitialised.current) {
            const heightInCm = getValues("height");
            const { feet, inches } = toImperial(heightInCm);
            const heightOverrideInCm = getValues("override_height");
            const { feet: overrideFeet, inches: overrideInches } =
                toImperial(heightOverrideInCm);
            setInitialImperialHeight({
                height: { feet, inches },
                overrideHeight: { feet: overrideFeet, inches: overrideInches },
            });
        }
    }, []);

    const {
        register,
        setValue,
        control,
        getValues,
        formState: { errors },
    } = useFormContext();
    return (
        <>
            <div className="form__group">
                <input type="hidden" {...register("id")} />
                <FormField errors={errors} name="version name">
                    <Input
                        type="text"
                        name="version_name"
                        placeholder="Default"
                        register={register}
                    />
                </FormField>
                <p className="info">
                    Your character can have multiple versions. This is the name
                    of the first one you will create by filling in this
                    character sheet.
                </p>
            </div>
            <div className="form__group">
                <FormField
                    errors={errors}
                    name="is_default"
                    displayName="Make this the default version ?"
                >
                    <Input
                        type="checkbox"
                        name="is_default"
                        placeholder="Is default?"
                        register={register}
                    />
                </FormField>
            </div>
            <div className="form__group">
                <FormField errors={errors} name="age">
                    <Input
                        type="text"
                        name="age"
                        placeholder="Age"
                        register={register}
                    />
                </FormField>
            </div>

            <div className="form__group">
                <FormField errors={errors} name="pronouns">
                    <Input
                        type="text"
                        name="pronouns"
                        placeholder="Pronouns"
                        register={register}
                    />
                </FormField>
            </div>
            <div className="form__group">
                <FormField errors={errors} name="about">
                    <Textarea
                        control={control}
                        characterLimit={500}
                        name="about"
                        placeholder="Give a summary of your character's behavior, likes and dislikes, current lifestyle, for example"
                        rows={20}
                    />
                </FormField>
            </div>

            <div className="form__group">
                <FormField errors={errors} name="body">
                    <FileInput
                        inputName="body"
                        value={bodyImage}
                        hideLabel={true}
                        parentFunction={(dataUrl) =>
                            setValue("body", dataUrl, { shouldDirty: true })
                        }
                    />
                </FormField>
            </div>
            <div className="form__group">
                <FormField
                    errors={errors}
                    name="is_nsfw"
                    displayName="Is this image nsfw ?"
                >
                    <Input
                        type="checkbox"
                        name="is_nsfw"
                        placeholder="Is NSFW ?"
                        register={register}
                    />
                </FormField>
                <p className="info">
                    <strong>Please tag responsibly.</strong> "NSFW" doesn't
                    necessarily mean gore, intimacy, etc; but anything not
                    "family-friendly".
                    <br />
                    The goal is not to censor your art and character.
                </p>
            </div>
            <div className="form__group">
                <FormField errors={errors} name="height">
                    {measurementUnit == "metric" ? (
                        <Input
                            type="number"
                            step={1}
                            name="height"
                            placeholder="190"
                            register={register}
                        />
                    ) : (
                        <>
                            <Input
                                type="hidden"
                                name="height"
                                placeholder="190"
                                register={register}
                            />
                            <FormField
                                name="height-feet"
                                displayName="Ft"
                                errors={errors}
                            >
                                <Input
                                    type="number"
                                    step={1}
                                    name="height-feet"
                                    placeholder="5"
                                    register={register}
                                    onChange={() => handleHeight("height")}
                                />
                            </FormField>
                            <FormField
                                name="height-inches"
                                displayName="In"
                                errors={errors}
                            >
                                <Input
                                    type="number"
                                    step={1}
                                    name="height-inches"
                                    placeholder="5"
                                    register={register}
                                    onChange={() => handleHeight("height")}
                                />
                            </FormField>
                        </>
                    )}
                </FormField>
                <FormField
                    errors={errors}
                    name="override_height"
                    displayName="Override Height ? (optional)"
                >
                    {measurementUnit == "metric" ? (
                        <Input
                            type="number"
                            step={1}
                            name="override_height"
                            placeholder="180"
                            register={register}
                        />
                    ) : (
                        <>
                            <Input
                                type="hidden"
                                name="override_height"
                                placeholder="190"
                                register={register}
                            />

                            <FormField
                                name="override_height-feet"
                                displayName="Ft"
                                errors={errors}
                            >
                                <Input
                                    type="number"
                                    step={1}
                                    name="override_height-feet"
                                    placeholder="5"
                                    register={register}
                                    onChange={() =>
                                        handleHeight("override_height")
                                    }
                                />
                            </FormField>

                            <FormField
                                name="override_height-inches"
                                displayName="In"
                                errors={errors}
                            >
                                <Input
                                    type="number"
                                    step={1}
                                    name="override_height-inches"
                                    placeholder="5"
                                    register={register}
                                    onChange={() =>
                                        handleHeight("override_height")
                                    }
                                />
                            </FormField>
                        </>
                    )}
                </FormField>
                <Hint title="Height VS Override height">
                    <span className="info text">
                        tldr; Override height = Physical height + accessories -
                        posture
                        <hr />
                        <br />
                        <p>
                            The override height is the height of your
                            character's <em>image</em>.<br />
                            For example, the body image you uploaded portayed
                            someone wearing 5cm heels, and their{" "}
                            <em>physical</em> height (just above) is 180cm, the
                            override height is be 185cm.
                            <br /> The same applies for a character who is
                            biologically 200cm tall but is always presented
                            hunched over, they would lose height so you'd fill
                            in 170cm.
                            <br /> This is the height that will then be used in
                            the height-chart to accurately display characters.
                        </p>
                    </span>
                </Hint>
            </div>
            <h4>Facial features (optional)</h4>
            <p className="info">
                What is the most defining trait of each of these features? What
                does it evoke in others?
            </p>
            <div className="form__group">
                <FormField errors={errors} name="ears">
                    <Input
                        type="text"
                        name="ears"
                        placeholder="Ears"
                        register={register}
                    />
                </FormField>
                {hints && (
                    <Hint title="hint">
                        <span>
                            Are they average sized ? Asymetrical? Impaired ?
                            Visibly injured ? <br /> Do they have any
                            accessories or tools (hearing-aid or earrings, for
                            ex) ?
                            <br />
                            What is one adjective that one would use to describe
                            them ?
                        </span>
                    </Hint>
                )}
            </div>
            <div className="form__group">
                <FormField errors={errors} name="eyes">
                    <Input
                        type="text"
                        name="eyes"
                        placeholder="Eyes"
                        register={register}
                    />
                </FormField>
                {hints && (
                    <Hint title="hint">
                        <span>
                            Again, think of the size, symmetry, colour,
                            accessories, possible injuries, etc.
                            <br />
                            Would starring into them fill one with warmth or a
                            sense of dread ?
                            <br />
                            Is there anything in nature that one would use to
                            describe them ? The moon, the sun, the stary sky or
                            the surface of a frosted lake at dusk ?
                            <br /> Or are they amazingly dull? Maybe average?
                            Maybe just green ?
                        </span>
                    </Hint>
                )}
            </div>
            <div className="form__group">
                <FormField errors={errors} name="faceshape">
                    <Input
                        type="text"
                        name="faceshape"
                        placeholder="Faceshape"
                        register={register}
                    />
                </FormField>
                {hints && (
                    <Hint title="hint">
                        <span>
                            Again, think of the size, symmetry, colour possible
                            injuries (perhaps old acne scars ?), etc.
                            <br />
                            If your character is human, would they be described
                            as rat? A wolf? A cat ?
                            <br />
                            Do they have any markings/ tattoos ?
                        </span>
                    </Hint>
                )}
            </div>
            <div className="form__group">
                <FormField errors={errors} name="mouth">
                    <Input
                        type="text"
                        name="mouth"
                        placeholder="Mouth"
                        register={register}
                    />
                </FormField>
                {hints && (
                    <Hint title="hint">
                        <span>
                            Are their lips (top, bottom or both) full or thin ?
                            Scars ?
                            <br />
                            Do they have all their teeth ? Is there anything
                            particular about them ? Big canines, gaps, overlaps,
                            accessories ?
                            <br /> Are they pearly whites, yellowed or any other
                            color ?
                            <br />
                            Do they have a funky looking tongue that changes
                            color at night ?
                            <br />
                            Does their smile feel like being home ? Is it
                            unsettling? Does it feel forced, and hides sorrow ?
                        </span>
                    </Hint>
                )}
            </div>
            <div className="form__group">
                <FormField errors={errors} name="nose">
                    <Input
                        type="text"
                        name="nose"
                        placeholder="Nose"
                        register={register}
                    />
                </FormField>
                {hints && (
                    <Hint title="hint">
                        <span>
                            Do they have a nose ? Does it look broken ? Is it a
                            button-nose ? Aquiline ? Long ? Flat ? <br />
                            Does it easily get red when the weather is cold ?
                            <br />
                            Is the bridge of their nose thick, making them look
                            almost feline ?
                            <br />
                            Does it have a bump ? Is the tip of their nose sharp
                            or round ? Does it turn in or out ?
                        </span>
                    </Hint>
                )}
            </div>
            <div className="form__group">
                <FormField errors={errors} name="flaws">
                    <>
                        <Input
                            type="text"
                            name="flaws.0"
                            placeholder="Flaws"
                            register={register}
                        />
                        <Input
                            type="text"
                            name="flaws.1"
                            placeholder="Flaws"
                            register={register}
                        />
                        <Input
                            type="text"
                            name="flaws.2"
                            placeholder="Flaws"
                            register={register}
                        />
                    </>
                </FormField>
            </div>
            <div className="form__group">
                <FormField errors={errors} name="qualities">
                    <>
                        <Input
                            type="text"
                            name="qualities.0"
                            placeholder="Qualities"
                            register={register}
                        />
                        <Input
                            type="text"
                            name="qualities.1"
                            placeholder="Qualities"
                            register={register}
                        />
                        <Input
                            type="text"
                            name="qualities.2"
                            placeholder="Qualities"
                            register={register}
                        />
                    </>
                </FormField>
            </div>
        </>
    );
};
export default VersionForm;
